<script setup>
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'

import { computed, onMounted, ref, shallowRef, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { CalendarField } from '@/@vxapp/@Enums/PHP/ServicesV2/Calendar/DataProviders/CalendarField'
import { setPageTitle } from '@/@core/utils/pageMetaUtils'

setPageTitle(PageMeta.Calendar.Full.Title)
definePage({ meta: PageMeta.Calendar.Full.Meta })

import FullCalendar from '@fullcalendar/vue3'
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'
import { useCalendarStore } from '@/@vxapp/Calendar/useCalendarStore'
import { useFullCalendar } from '@/@vxapp/Calendar/useFullCalendar'
import { useResponsiveLeftSidebar } from '@core/composable/useResponsiveSidebar'
import CalendarEventHandler from '@/components/Drawers/Calendar/CalendarEventHandler.vue'
import CalendarMobile from '@/views/apps/calendar/CalendarMobile.vue'
import { canDelete } from '@/@layouts/plugins/casl'
import { useNotificationsStore } from '@/@vxapp/useNotificationsStore'
import { ServiceType } from '@/@vxapp/@Enums/ServiceType'

const log = createLogger('index')

const drawerNames = ref({
  [PageMeta.Rules.Endpoint.Name]: 'Booking Rule',
  [PageMeta.Quotes.Endpoint.Name]: 'Quote Calendar Entry',
})

const linkService = ref(null)
const drawerTitle = ref('')
const drawerName = ref('Event')
const drawerTabToOpen = ref(null)
const isRule = ref(false)
const isUpdate = ref(false)
const event = ref(null)
const isEventHandlerSidebarActive = ref(false)
const { isLeftSidebarOpen } = useResponsiveLeftSidebar()

const calendarStore = useCalendarStore()
const notify = useNotificationsStore()

const setEventRef = async (calendarData, dateToUse) => {
  drawerName.value = 'Event'

  if (!calendarData) {
    drawerTitle.value = 'Add New'
    isRule.value = false

    const response = await calendarStore.fetchTemplate()

    event.value = JSON.parse(JSON.stringify(response.template))

    if (dateToUse) {
      event.value.calendarInstallDate = dateToUse
      event.value.calendarRemovalDate = dateToUse
    }

    isUpdate.value = false

    return
  }

  drawerTitle.value = 'Update'
  isUpdate.value = true
  event.value = JSON.parse(JSON.stringify(calendarData))

  if (
    event.value.ruleGUIDs &&
    Array.isArray(event.value.ruleGUIDs) &&
    event.value.ruleGUIDs.length > 0 &&
    event.value.ruleGUIDs.every(guid => typeof guid === 'string' && guid.length > 0)
  ) {
    drawerName.value = 'Rule'
    isRule.value = true
  } else {
    isRule.value = false
  }

  log.debug('=========== setEventRef ===========')
  log.debug('isUpdate', isUpdate.value)
  log.debug('isRule', isRule.value)
}

const eventAdded = async () => {
  refetchEvents()
}

const eventUpdated = async () => {
  refetchEvents()
}

const removeEvent = async calendarEventHandlerProps => {
  const localProps = calendarEventHandlerProps.props

  log.debug(`[Calendar] removeEvent`, localProps)

  const drawerName = localProps.drawerName
  const eventGUID = localProps.event[CalendarField.DB_IDENTIFIER]

  if (!eventGUID) {
    notify.error(ServiceType.Calendar.Error.NO_GUID)

    return
  }

  if (drawerName === 'Rule') {
    if (!canDelete(ServiceType.Calendar.Page.DeleteRule.Meta.subject)) {
      notify.error(ServiceType.Calendar.AuthError.INSUFFICIENT_PERMISSIONS)

      return
    }

    const { responseCode, message } = await calendarStore.delete(eventGUID)
    if (responseCode === 200) {
      notify.success(message)
      refetchEvents()
    } else {
      notify.error(ServiceType.Calendar.Error.DELETE_FAILED)
    }
  } else {
    notify.warn(ServiceType.Calendar.Error.NOT_CONFIGURED)
  }
}

const { refCalendar, calendarOptions } = useFullCalendar(
  calendarStore,
  isEventHandlerSidebarActive,
  isLeftSidebarOpen,
  eventUpdated,
  setEventRef
)

// Phones get their own calendar presentation; both share the options, handlers and drawers.
const { mdAndUp } = useDisplay()

const desktopViews = ['dayGridMonth', 'timeGridWeek', 'timeGridDay', 'listMonth']

const desktopOptions = shallowRef(calendarOptions)

// Crossing the breakpoint remounts the calendar; a phone-only view falls back to the month.
watch(mdAndUp, desktop => {
  if (!desktop) return

  const { viewType, currentDate } = calendarStore.currentState

  desktopOptions.value = {
    ...calendarOptions,
    initialView: desktopViews.includes(viewType) ? viewType : 'dayGridMonth',
    initialDate: currentDate,
  }
})

/*
 * The Stores composable keeps the API of the calendar mounted first. After a breakpoint change
 * the page talks to the calendar now shown, and refreshes it when the job-type filter changes.
 */
const currentApi = () => refCalendar.value?.getApi()
let firstApi = null

onMounted(() => {
  firstApi = currentApi()
})

watch(
  () => calendarStore.selectedJobTypes,
  () => {
    const api = currentApi()

    if (api && api !== firstApi) api.refetchEvents()
  }
)

const refetchEvents = () => currentApi()?.refetchEvents()
const jumpToDate = date => currentApi()?.gotoDate(new Date(date))

const checkAll = computed({
  get: () => calendarStore.selectedJobTypes.length === calendarStore.availableJobTypes.length,
  set: val => {
    if (val) calendarStore.selectedJobTypes = calendarStore.availableJobTypes.map(i => i.id)
    else if (calendarStore.selectedJobTypes.length === calendarStore.availableJobTypes.length)
      calendarStore.selectedJobTypes = []
  },
})

const jumpToDateFn = date => {
  jumpToDate(date)
}

const handleShortcutCardClick = shortcut => {
  handleIconClicked(shortcut)
}

const handleAddEventButtonClick = () => {
  handleeventAddedBtnPressed('updateCreate')
}

const handleBookingRulesButtonClick = () => {
  handleBookingRuleBtnPressed('allRules')
}

const handleBookingRuleBtnPressed = async (tabToOpen, serviceToLink = null) => {
  await setEventRef()
  linkService.value = serviceToLink
  drawerTabToOpen.value = tabToOpen
  drawerName.value = drawerNames.value[PageMeta.Rules.Endpoint.Name]
  drawerTitle.value = 'Apply'
  isRule.value = true
  isUpdate.value = false
  isEventHandlerSidebarActive.value = true
}

const handleeventAddedBtnPressed = async (tabToOpen, serviceToLink = null) => {
  await setEventRef()
  linkService.value = serviceToLink
  drawerTabToOpen.value = tabToOpen
  drawerName.value = 'Event'
  drawerTitle.value = 'Add New'
  isUpdate.value = false
  isRule.value = false
  isEventHandlerSidebarActive.value = true
}

/* shortcuts */

const handleIconClicked = item => {
  if (item.icon == PageMeta.Calendar.Add.Icon) {
    handleeventAddedBtnPressed('updateCreate')
  }

  if (item.icon == PageMeta.Rules.List.Icon) {
    handleBookingRuleBtnPressed('allRules')
  }

  if (item.icon === PageMeta.Calendar.AddRule.Icon) {
    handleBookingRuleBtnPressed('updateCreate', PageMeta.Rules.Endpoint.Name)
  }

  if (item.icon === PageMeta.Calendar.AddQuote.Icon) {
    log.debug('[Calendar] Creating Quote Event')
    handleeventAddedBtnPressed('updateCreate', ServiceType.Quotes.Name)
  }
}

const shortcuts = computed(() => [
  {
    icon: PageMeta.Calendar.Add.Icon,
    color: 'success',
    title: PageMeta.Calendar.Add.Title,
    toolTip: 'Create A New Calendar Entry',
  },
  {
    icon: PageMeta.Calendar.AddQuote.Icon,
    color: 'success',
    title: PageMeta.Calendar.AddQuote.Title,
    toolTip: 'Create a calendar entry for a Quote',
  },
  {
    icon: PageMeta.Calendar.AddRule.Icon,
    color: 'error',
    title: PageMeta.Calendar.AddRule.Title,
    toolTip: 'Create a booinking rule entry in the calendar',
  },
  {
    icon: PageMeta.Rules.List.Icon,
    color: 'error',
    title: PageMeta.Rules.List.Title,
    toolTip: 'Manage Booking Rules',
  },
])

/* *** */
</script>

<template>
  <div class="calendar-page">
    <!-- header -->
    <VCard
      title="Shortcuts"
      flat
      class="mb-6 app-tile calendar-shortcuts"
      data-testid="calendar-shortcuts"
    >
      <VRow class="ma-0 mt-n1">
        <VCol
          v-for="(shortcut, index) in shortcuts"
          :key="shortcut.title"
          cols="6"
          lg="2"
          class="text-center border-t cursor-pointer pa-6 shortcut-icon"
          :class="(index + 1) % 2 ? 'border-e' : ''"
          @click="() => handleShortcutCardClick(shortcut)"
        >
          <ParentToolTip :message="shortcut.toolTip">
            <VAvatar
              variant="tonal"
              size="50"
            >
              <VIcon
                size="26"
                :color="shortcut.color"
                :icon="shortcut.icon"
              />
            </VAvatar>

            <VCardTitle class="text-base font-weight-medium mt-3 mb-0">
              {{ shortcut.title }}
            </VCardTitle>
          </ParentToolTip>
        </VCol>
      </VRow>
    </VCard>
    <!-- // header -->

    <section aria-labelledby="calendar-heading">
      <header class="calendar-heading">
        <p class="calendar-heading__eyebrow">Schedule</p>
        <h2
          id="calendar-heading"
          class="calendar-heading__title"
        >
          Calendar
        </h2>
      </header>
      <!-- `z-index: 0` Allows overlapping vertical nav on calendar -->
      <VLayout
        v-if="mdAndUp"
        class="calendar-layout"
        style="z-index: 0"
        data-testid="calendar-desktop"
      >
        <VNavigationDrawer
          v-model="isLeftSidebarOpen"
          width="292"
          absolute
          touchless
          location="start"
          class="scrollable-content app-tile calendar-filters"
          data-testid="calendar-filters"
          :temporary="$vuetify.display.mdAndDown"
        >
          <PerfectScrollbar :options="{ wheelPropagation: true }">
            <VCardItem>
              <VBtn
                block
                prepend-icon="tabler-plus"
                class="mb-3"
                variant="outlined"
                color="success"
                @click="handleAddEventButtonClick"
              >
                Add event
              </VBtn>

              <VBtn
                block
                :prepend-icon="PageMeta.Rules.Icon"
                color="error"
                variant="outlined"
                @click="handleBookingRulesButtonClick"
              >
                Booking Rules
              </VBtn>
            </VCardItem>

            <VDivider />

            <div class="d-flex align-center justify-center pa-2">
              <AppDateTimePicker
                :model-value="new Date().toJSON().slice(0, 10)"
                :config="{ inline: true }"
                class="calendar-date-picker"
                @update:model-value="jumpToDateFn"
              />
            </div>

            <VDivider />
            <div
              v-if="calendarStore?.selectedJobTypes && calendarStore?.availableJobTypes"
              class="pa-6"
            >
              <VCardTitle class="text-lg font-weight-medium mb-4">Filter By Job Type</VCardTitle>

              <div class="mb-10 d-flex flex-column calendars-checkbox">
                <VCheckbox
                  v-model="checkAll"
                  label="View all"
                />
                <VCheckbox
                  v-for="calendar in calendarStore.availableJobTypes"
                  :key="calendar.title"
                  v-model="calendarStore.selectedJobTypes"
                  :value="calendar.id"
                  :color="`rgb(${calendar.color})`"
                  :label="calendar.title"
                />
              </div>
            </div>
          </PerfectScrollbar>
        </VNavigationDrawer>

        <VMain>
          <VCard
            flat
            class="app-tile calendar-surface"
            data-testid="calendar-surface"
          >
            <FullCalendar
              ref="refCalendar"
              :options="desktopOptions"
            />
          </VCard>
        </VMain>
      </VLayout>
      <CalendarMobile
        v-else
        ref="refCalendar"
        :calendar-options="calendarOptions"
        @add-event="handleAddEventButtonClick"
        @booking-rules="handleBookingRulesButtonClick"
      />
    </section>

    <CalendarEventHandler
      v-if="event"
      v-model:is-drawer-open="isEventHandlerSidebarActive"
      :drawer-name="drawerName"
      :drawer-title="drawerTitle"
      :is-update="isUpdate"
      :is-rule="isRule"
      :event="event"
      :drawer-tab-to-open="drawerTabToOpen"
      :link-service="linkService"
      @event-added="eventAdded"
      @event-updated="eventUpdated"
      @remove-event="removeEvent"
    />
  </div>
</template>

<style lang="scss">
@use '@core/scss/template/libs/full-calendar';

.calendars-checkbox {
  .v-label {
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
    opacity: var(--v-high-emphasis-opacity);
  }
}

.calendar-add-event-drawer {
  &.v-navigation-drawer:not(.v-navigation-drawer--temporary) {
    border-end-start-radius: 0.375rem;
    border-start-start-radius: 0.375rem;
  }
}

/* Dashboard tile language: flat surfaces, hairline edges and quiet sentence-case controls.
   `.v-application` outranks the template's `body .fc` toolbar rules. */
.v-application .calendar-page {
  .calendar-heading {
    margin-block-end: 12px;
  }

  .calendar-heading__eyebrow {
    margin: 0;
    color: rgba(var(--v-theme-on-surface), 0.5);
    font-family: var(--app-font-ui);
    font-size: 12px;
    font-weight: 600;
    line-height: 1.4;
  }

  .calendar-heading__title {
    margin: 0;
    color: rgb(var(--v-theme-on-surface));
    font-family: var(--app-font-ui);
    font-size: 1.25rem;
    font-weight: 600;
  }

  .calendar-shortcuts .shortcut-icon {
    border-color: var(--app-tile-border) !important;
    transition: background-color 150ms ease;
  }

  .calendar-shortcuts .shortcut-icon:hover {
    background: rgba(var(--v-theme-on-surface), 0.03);
  }

  /* A temporary filter drawer covers the calendar, so it keeps an opaque surface. */
  .calendar-filters.v-navigation-drawer--temporary {
    background: rgb(var(--v-theme-background));
  }

  .calendar-layout:has(.calendar-filters:not(.v-navigation-drawer--temporary)) .calendar-surface {
    margin-inline-start: 16px;
  }

  .app-tile:hover {
    border-color: var(--app-tile-border-hover);
  }

  .fc {
    --fc-page-bg-color: transparent;
    --fc-border-color: var(--app-tile-border);
    --fc-neutral-bg-color: var(--app-tile-bg);
    --fc-today-bg-color: rgba(var(--v-theme-primary), 0.08);

    font-family: var(--app-font-ui);

    .fc-toolbar-title {
      font-family: var(--app-font-ui);
      font-weight: 600;
    }

    .fc-list .fc-list-table {
      border-block-end: 1px solid var(--app-tile-border);
    }

    .fc-toolbar-chunk .fc-button-group .fc-button {
      border-radius: 10px;
      font-family: var(--app-font-ui);
      transition: background-color 150ms ease;

      &:hover {
        background-color: rgba(var(--v-theme-on-surface), 0.06);
      }
    }

    .fc-toolbar-chunk:last-child .fc-button-group {
      padding: 2px;
      border: 1px solid var(--app-tile-border);
      border-radius: 12px;
      gap: 2px;

      .fc-button,
      .fc-button:active {
        border: 0;
        border-radius: 10px;
        background-color: transparent;
        color: rgba(var(--v-theme-on-surface), 0.7);
        font-size: 0.875rem;
        font-weight: 600;
        letter-spacing: 0;
        padding-inline: 0.875rem;

        &:hover {
          background-color: rgba(var(--v-theme-on-surface), 0.06);
        }

        &.fc-button-active {
          background-color: rgba(var(--v-theme-primary), 0.12);
          color: rgb(var(--v-theme-primary));
        }
      }
    }
  }
}

/* For list view - make the dot fully opaque */
.fc-list-event-dot {
  opacity: 1 !important;
  background-color: var(--fc-event-border-color) !important;
}

.calendar-date-picker {
  display: none;

  + .flatpickr-input {
    + .flatpickr-calendar.inline {
      border: none;
      box-shadow: none;

      .flatpickr-months {
        border-block-end: none;
      }
    }
  }

  & ~ .flatpickr-calendar .flatpickr-weekdays {
    margin-block: 0 4px;
  }
}
</style>

<style lang="scss" scoped>
.v-layout {
  overflow: visible !important;

  .v-card {
    overflow: visible;
  }
}
</style>
